/**
 * App Layout
 * Main authenticated layout with sidebar
 */
import React from 'react';
import { Outlet, Navigate, useLocation, Link } from 'react-router';
import { Sidebar } from '../organisms/Sidebar';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n';
import { Toaster } from '../ui/sonner';
import { FeedbackFAB } from '../molecules/FeedbackFAB';
import { LoadingSpinner } from '../atoms/LoadingSpinner';
import { useProviderStatus } from '../../hooks/useProviderStatus';
import { AlertTriangle, KeyRound, Shield, X } from 'lucide-react';
import { TrialBanner } from '../molecules/TrialBanner';
import { SetupBonusBanner } from '../molecules/SetupBonusBanner';

export function AppLayout() {
  const { isAuthenticated, isLoading, apiKey, user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const isWorkflowRoute = location.pathname.startsWith('/workflow');
  const { providerStatus } = useProviderStatus(apiKey);
  const [bannerDismissed, setBannerDismissed] = React.useState(false);
  const [configBannerDismissed, setConfigBannerDismissed] = React.useState(false);
  const [proxyBannerDismissed, setProxyBannerDismissed] = React.useState(false);
  const isAdmin = user?.role === 'admin';

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50 dark:bg-[#050505]">
        <LoadingSpinner />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Build issues list from configured providers that are not operational
  const issues = providerStatus?.providers.filter(
    (p) => p.configured && p.status !== 'operational' && p.status !== 'unknown'
  ) ?? [];
  const showBanner = issues.length > 0 && !bannerDismissed;

  // Only warn if Anthropic (required provider) is not configured
  const anthropicProvider = providerStatus?.providers.find((p) => p.name === 'anthropic');
  const showConfigBanner = anthropicProvider !== undefined && !anthropicProvider.configured && !configBannerDismissed;

  // Optional providers that are not configured (for informational display)
  const optionalUnconfigured = providerStatus?.providers.filter(
    (p) => p.name !== 'anthropic' && !p.configured
  ) ?? [];

  // Proxy warning: only show when proxy is enabled but credentials are missing
  const proxyEnabled = providerStatus?.proxy_enabled ?? false;
  const proxyConfigured = providerStatus?.proxy_configured ?? false;
  const showProxyBanner = proxyEnabled && !proxyConfigured && providerStatus !== null && !proxyBannerDismissed;

  return (
    <>
      <div className="flex h-[100dvh] w-full bg-gray-50 dark:bg-[#050505] text-gray-900 dark:text-white overflow-hidden font-sans transition-colors duration-300">
        <Sidebar />

        <main className={`flex-1 h-full p-4 md:p-6 pb-28 md:pb-6 relative ${isWorkflowRoute ? 'overflow-hidden' : 'overflow-auto'}`}>
          <div className="fixed inset-0 bg-[url('/noise.svg')] opacity-[0.04] dark:opacity-[0.06] pointer-events-none z-0"></div>
          <div className={`relative z-10 max-w-7xl mx-auto ${isWorkflowRoute ? 'h-full min-h-0 flex flex-col space-y-6 md:space-y-8' : 'space-y-6 md:space-y-8'}`}>
            {/* Global provider-issue banner */}
            {showBanner && (
              <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/60 px-4 py-3 flex items-start gap-3 animate-in slide-in-from-top duration-300">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-amber-800 dark:text-amber-200">
                  <span className="font-semibold">{t('aiProvider.issue')} — </span>
                  {issues.map((p, i) => (
                    <span key={p.name}>
                      {i > 0 && ', '}
                      <a
                        href={p.status_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-amber-900 dark:hover:text-amber-100"
                      >
                        {p.display_name}
                      </a>
                      {' '}
                      ({p.status === 'degraded' ? t('aiProvider.degradedPerformance') : t('aiProvider.outage')})
                    </span>
                  ))}
                  . {t('aiProvider.pipelineMayBeAffected')}
                </div>
                <button
                  onClick={() => setBannerDismissed(true)}
                  className="text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-200 p-0.5"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Missing Anthropic API key banner (required provider) */}
            {showConfigBanner && (
              <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/60 px-4 py-3 flex items-start gap-3 animate-in slide-in-from-top duration-300">
                <KeyRound className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-red-800 dark:text-red-200">
                  <span className="font-semibold">Anthropic API Key Required — </span>
                  The Anthropic (Claude) API key is not configured. This is required for the scraper to function.
                  {optionalUnconfigured.length > 0 && (
                    <span className="text-red-600 dark:text-red-300">
                      {' '}({optionalUnconfigured.map((p) => p.display_name).join(', ')} also not configured — these are optional and will be disabled.)
                    </span>
                  )}
                  {' '}
                  {isAdmin ? (
                    <>
                      Go to{' '}
                      <Link to="/settings" className="underline hover:text-red-900 dark:hover:text-red-100">
                        Settings
                      </Link>{' '}
                      to add your API key.
                    </>
                  ) : (
                    'Contact your administrator to configure the API key.'
                  )}
                </div>
                <button
                  onClick={() => setConfigBannerDismissed(true)}
                  className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 p-0.5"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Proxy configuration banner — only when enabled but misconfigured */}
            {showProxyBanner && (
              <div className="rounded-lg border border-orange-300 dark:border-orange-700 bg-orange-50 dark:bg-orange-950/60 px-4 py-3 flex items-start gap-3 animate-in slide-in-from-top duration-300">
                <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5 shrink-0" />
                <div className="flex-1 text-sm text-orange-800 dark:text-orange-200">
                  <span className="font-semibold">Proxy Credentials Missing — </span>
                  Proxy is enabled but at least ISP proxy credentials are required.
                  Residential proxy credentials are optional but recommended for heavily protected platforms.
                  {isAdmin ? (
                    <>
                      {' '}Go to{' '}
                      <Link to="/settings" className="underline hover:text-orange-900 dark:hover:text-orange-100">
                        Settings
                      </Link>{' '}
                      to configure proxy credentials.
                    </>
                  ) : (
                    ' Contact your administrator to configure proxy credentials.'
                  )}
                </div>
                <button
                  onClick={() => setProxyBannerDismissed(true)}
                  className="text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200 p-0.5"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Trial period banner — admin only, SaaS only */}
            <TrialBanner />

            {/* First-month setup bonus banner — admin only, SaaS only */}
            <SetupBonusBanner />

            {isWorkflowRoute ? (
              <div className="flex-1 min-h-0">
                <Outlet />
              </div>
            ) : (
              <Outlet />
            )}
          </div>
        </main>
      </div>

      <Toaster position="top-right" richColors closeButton />
      <FeedbackFAB />

      {/* Company signature watermark */}
      <img
        src="/BOW_Sales_Background.png"
        alt="BOW Sales"
        className="fixed bottom-0 right-0 opacity-10 dark:opacity-20 pointer-events-none select-none z-50"
      />
    </>
  );
}

export default AppLayout;
